import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import CategoryBadge from '../../components/candidates/CategoryBadge';
import LeadImportModal from '../../components/leads/LeadImportModal';
import VerifyLeadModal from '../../components/leads/VerifyLeadModal';
import { useRowSelection } from '../../components/requests/BulkSelection';
import { selectColumn } from '../../components/requests/RequestActions';
import Can from '../../auth/Can';
import { usePermission } from '../../auth/usePermission';
import { useAuth } from '../../auth/AuthContext';
import RejectDialog from '../../components/candidates/RejectDialog';
import { listLeads, deleteLead, convertLead, rejectLead } from '../../api/leads';
import CandidateFilterBar, { useCandidateFilters } from '../../components/filters/CandidateFilterBar';
import { listLeadSources } from '../../api/masters';
import { pushToast } from '../../components/ui/toastBus';
import { formatValidationErrors } from '../../utils/errors';
import SearchSummary, { withMatchColumn } from '../../components/ui/SearchMatch';

const STATUS_TABS = [
  { value: 'new', label: 'New' },
  { value: 'converted', label: 'Converted' },
  { value: 'rejected', label: 'Rejected' },
  { value: '', label: 'All' },
];

export default function LeadsList() {
  const queryClient = useQueryClient();
  const { can } = usePermission();
  const { user } = useAuth();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(STATUS_TABS[0].value);
  const filters = useCandidateFilters({ withSteps: false });
  const [sourceId, setSourceId] = useState('');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null); // a lead row, or 'bulk'
  const [importOpen, setImportOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null); // 'send' | 'delete'
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => setPage(1), [filters.params]);
  const sourcesQuery = useQuery({ queryKey: ['lead-sources', { include_inactive: true }], queryFn: () => listLeadSources({ include_inactive: true }) });

  const params = {
    search: search || undefined,
    status: status || undefined,
    ...filters.params,
    lead_source_id: sourceId || undefined,
    page,
  };

  const leadsQuery = useQuery({ queryKey: ['leads', params], queryFn: () => listLeads(params) });

  const deleteMutation = useMutation({
    mutationFn: deleteLead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      pushToast({ type: 'success', message: 'Lead deleted.' });
      setDeleteTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not delete lead.' });
      setDeleteTarget(null);
    },
  });

  const sendMutation = useMutation({
    mutationFn: convertLead,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      const missing = result.missing_documents;
      pushToast({
        type: missing?.length ? 'error' : 'success',
        message: missing?.length
          ? `Sent to candidate ${result.candidate.reference_no}, but ${missing.length} document(s) are still missing.`
          : `Sent to candidate ${result.candidate.reference_no}.`,
      });
      setSendTarget(null);
    },
    onError: (err) => {
      const message = err.response?.data?.errors
        ? `Complete the profile before sending — ${formatValidationErrors(err.response.data.errors)}`
        : err.response?.data?.message || 'Could not send lead.';
      pushToast({ type: 'error', message });
      setSendTarget(null);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }) => rejectLead(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      pushToast({ type: 'success', message: 'Lead rejected.' });
      setRejectTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not reject lead.' });
      setRejectTarget(null);
    },
  });

  const canReject = (row) => can('leads.reject-all') || (can('leads.reject-own') && row.created_by_id === user?.id);
  const canRejectRow = (row) => row.status === 'new' && canReject(row);
  const canSend = (row) => can('leads.send-all') || (can('leads.send-own') && row.created_by_id === user?.id);
  const canDelete = (row) => can('leads.delete-all') || (can('leads.delete-own') && row.created_by_id === user?.id);

  const rows = leadsQuery.data?.data || [];
  const canSendRow = (row) => row.status === 'new' && row.verification_status === 'verified' && canSend(row);
  const selection = useRowSelection(rows, (r) => r.id, (r) => canSendRow(r) || canRejectRow(r) || canDelete(r));
  const selectedRows = rows.filter((r) => selection.selectedIds.includes(r.id));
  const sendable = selectedRows.filter(canSendRow);
  const rejectable = selectedRows.filter(canRejectRow);
  const deletable = selectedRows.filter(canDelete);
  const bulkTargets = bulkAction === 'send' ? sendable : bulkAction === 'reject' ? rejectable : deletable;

  // Runs the existing single-lead call one lead at a time so a failure on one doesn't stop the rest.
  const runBulk = async (action, targets, reason) => {
    const fn = action === 'send' ? convertLead : action === 'reject' ? (id) => rejectLead(id, reason) : deleteLead;
    const verb = action === 'send' ? 'sent to candidate' : action === 'reject' ? 'rejected' : 'deleted';
    setBulkBusy(true);
    let failed = 0;
    let firstError = '';
    for (const lead of targets) {
      try {
        await fn(lead.id);
      } catch (err) {
        failed += 1;
        firstError =
          firstError ||
          (err.response?.data?.errors
            ? `${lead.reference_no}: ${formatValidationErrors(err.response.data.errors)}`
            : err.response?.data?.message || '');
      }
    }
    setBulkBusy(false);
    setBulkAction(null);
    setRejectTarget(null);
    selection.clear();
    queryClient.invalidateQueries({ queryKey: ['leads'] });
    const ok = targets.length - failed;
    pushToast(
      failed === 0
        ? { type: 'success', message: `${ok} lead${ok === 1 ? '' : 's'} ${verb}.` }
        : { type: 'error', message: `${ok} ${verb}, ${failed} failed${firstError ? ` — ${firstError}` : '.'}` }
    );
  };

  const columns = [
    {
      ...selectColumn(selection, (r) => r.id, (r) => canSendRow(r) || canRejectRow(r) || canDelete(r)),
      label: (
        <input
          type="checkbox"
          checked={selection.allSelected}
          onChange={selection.toggleAll}
          disabled={selection.selectableCount === 0}
          aria-label="Select all leads on this page"
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
      ),
    },
    {
      key: 'reference_no',
      label: 'Reference No',
      render: (row) => (
        <Link to={`/leads/${row.id}`} className="font-medium text-primary hover:underline">
          {row.reference_no}
        </Link>
      ),
    },
    { key: 'full_name', label: 'Full Name' },
    { key: 'contact_no', label: 'Contact No' },
    { key: 'category', label: 'Category', render: (row) => (row.category ? <CategoryBadge category={row.category} /> : <span className="text-gray-400">—</span>) },
    { key: 'source', label: 'Source', render: (row) => row.source || <span className="text-gray-400">—</span> },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <div>
          <Badge tone={row.status === 'converted' ? 'green' : row.status === 'rejected' ? 'red' : 'gray'}>{row.status}</Badge>
          {row.status === 'rejected' && row.rejection_reason && (
            <div className="mt-0.5 max-w-[14rem] truncate text-xs text-red-600" title={row.rejection_reason}>
              {row.rejection_reason}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'verification_status',
      label: 'Verification',
      render: (row) =>
        row.status === 'new' ? (
          <Badge tone={row.verification_status === 'verified' ? 'green' : row.verification_status === 'rejected' ? 'red' : 'gray'}>
            {row.verification_status}
          </Badge>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    { key: 'created_by', label: 'Created By' },
    { key: 'created_at', label: 'Created', render: (row) => new Date(row.created_at).toLocaleDateString() },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-3 text-sm">
          <Link to={`/leads/${row.id}`} className="text-primary hover:underline">
            View
          </Link>
          {row.status === 'new' && can('leads.verify') && (
            <button onClick={() => setVerifyTarget(row)} className="font-medium text-green-700 hover:underline">
              Verify
            </button>
          )}
          {row.status === 'new' && canSend(row) && (
            <button
              onClick={() => setSendTarget(row)}
              disabled={row.verification_status !== 'verified'}
              title={row.verification_status !== 'verified' ? 'Verify this applicant first' : undefined}
              className="text-gray-600 hover:underline disabled:cursor-not-allowed disabled:text-gray-300 disabled:no-underline"
            >
              Send to Candidate
            </button>
          )}
          {canRejectRow(row) && (
            <button onClick={() => setRejectTarget(row)} className="text-amber-700 hover:underline">
              Reject
            </button>
          )}
          {canDelete(row) && (
            <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline">
              Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Applicants"
        actions={
          <Can perm="leads.create">
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setImportOpen(true)}>
                Bulk Upload
              </Button>
              <Link to="/leads/new">
                <Button>Register Lead</Button>
              </Link>
            </div>
          </Can>
        }
      />

      <LeadImportModal open={importOpen} onClose={() => setImportOpen(false)} />
      <VerifyLeadModal lead={verifyTarget} onClose={() => setVerifyTarget(null)} />

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => {
              setStatus(tab.value);
              setPage(1);
            }}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              status === tab.value
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <CandidateFilterBar {...filters.barProps}>
        <Input
          placeholder="Search name, reference, passport, contact…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-72"
        />
        <Select
          value={sourceId}
          onChange={(e) => {
            setSourceId(e.target.value);
            setPage(1);
          }}
          className="w-48"
        >
          <option value="">All sources</option>
          {(sourcesQuery.data || []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </CandidateFilterBar>

      {selection.selectedIds.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-2 text-sm">
          <span className="font-medium text-gray-700">{selection.selectedIds.length} selected</span>
          <Button variant="secondary" onClick={() => setBulkAction('send')} disabled={sendable.length === 0}>
            Send to Candidate ({sendable.length})
          </Button>
          <Button variant="secondary" onClick={() => setRejectTarget('bulk')} disabled={rejectable.length === 0}>
            Reject ({rejectable.length})
          </Button>
          <Button variant="danger" onClick={() => setBulkAction('delete')} disabled={deletable.length === 0}>
            Delete ({deletable.length})
          </Button>
          <button onClick={selection.clear} className="ml-auto text-gray-500 hover:underline">
            Clear
          </button>
        </div>
      )}

      <SearchSummary search={search} total={leadsQuery.data?.total} rows={rows} paged />

      <Table
        columns={withMatchColumn(columns, search)}
        data={rows}
        isLoading={leadsQuery.isLoading}
        pagination={leadsQuery.data}
        onPageChange={setPage}
        emptyMessage="No leads found."
      />

      <RejectDialog
        open={!!rejectTarget}
        onClose={() => !(bulkBusy || rejectMutation.isPending) && setRejectTarget(null)}
        actionLabel={rejectTarget === 'bulk' ? `Reject ${rejectable.length} lead(s)` : 'Reject lead'}
        loading={bulkBusy || rejectMutation.isPending}
        onConfirm={(reason) => {
          if (rejectTarget === 'bulk') {
            runBulk('reject', rejectable, reason);
          } else {
            rejectMutation.mutate({ id: rejectTarget.id, reason });
          }
        }}
      />

      <ConfirmDialog
        open={!!bulkAction && bulkAction !== 'reject'}
        onClose={() => !bulkBusy && setBulkAction(null)}
        onConfirm={() => runBulk(bulkAction, bulkTargets)}
        title={bulkAction === 'send' ? 'Send selected to Candidate' : 'Delete selected leads'}
        message={
          bulkAction === 'send'
            ? `Send ${bulkTargets.length} lead(s) to Candidate? This creates a candidate record for each and starts the workflow.`
            : `Delete ${bulkTargets.length} lead(s)? This cannot be undone.`
        }
        confirmLabel={bulkAction === 'send' ? 'Send' : 'Delete'}
        variant={bulkAction === 'send' ? 'primary' : undefined}
        loading={bulkBusy}
      />

      <ConfirmDialog
        open={!!sendTarget}
        onClose={() => setSendTarget(null)}
        onConfirm={() => sendMutation.mutate(sendTarget.id)}
        title="Send to Candidate"
        message={`Send ${sendTarget?.full_name} (${sendTarget?.reference_no}) to Candidate? This creates a candidate record and starts the workflow.`}
        confirmLabel="Send"
        variant="primary"
        loading={sendMutation.isPending}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete lead"
        message={`Delete ${deleteTarget?.full_name} (${deleteTarget?.reference_no})? This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
